import { useReactiveVar } from '@apollo/client';

import { userStateVar } from 'core/state/userState';
import { UserRole } from 'types';

/**
 * Capability booleans for the Student surfaces (list + card), derived from the
 * current user's roles. Mirrors `useGroupPermissions`. Hiding a control is a UX
 * affordance only — the backend remains the authority on each mutation/export.
 */
export interface IStudentPermissions {
  /** Admin, FinanceManager, AccountManager (SalesManager: read). */
  canViewFinance: boolean;
  /** Same set as `canViewFinance` — gates ALL export controls (Req 21.5). */
  canExportFinance: boolean;
  /** Admin, FinanceManager, AccountManager. */
  canRecordPayment: boolean;
  /**
   * Link an EXISTING Fakturownia invoice to a взнос. Admin, FinanceManager,
   * AccountManager. Creates nothing.
   */
  canIssueInvoice: boolean;
  /**
   * Issue a REAL Fakturownia VAT document («Выставить фактуру»). Same role
   * set as `canIssueInvoice`, but kept as its own capability because this is
   * the only student-card action that creates an irreversible fiscal document
   * — so it can be narrowed (or flag-gated) without touching the linking
   * flow. The backend is the authority (`FAKTUROWNIA_ACCESS_ROLES`).
   */
  canIssueFaktura: boolean;
  /** Admin, AccountManager, SalesManager. */
  canAddAgreement: boolean;
  /** Admin, AccountManager, SalesManager. */
  canChangeGroup: boolean;
  /** Admin, AccountManager, SalesManager. */
  canContactEmail: boolean;
  /** Admin, FinanceManager, AccountManager, SalesManager — block LMS access. */
  canBlockLmsAccess: boolean;
  /**
   * Admin, AccountManager, Methodist (spec 0038) — read this student's survey
   * answers. Narrower than the card itself: the tab pairs a named student
   * with their verbatim opinion of a named teacher, so Sales and Finance can
   * open the card but must not see the tab at all.
   */
  canReadStudentSurveys: boolean;
}

const hasAnyRole = (roles: UserRole[], allowed: UserRole[]): boolean =>
  roles.some((role) => allowed.includes(role));

/**
 * Reads `userStateVar().me.roles` and maps `UserRole` to capability booleans
 * for the Student surfaces.
 */
export const useStudentPermissions = (): IStudentPermissions => {
  const userState = useReactiveVar(userStateVar);
  const roles = (userState.me?.roles ?? []) as UserRole[];

  const canViewFinance = hasAnyRole(roles, [
    UserRole.ADMIN,
    UserRole.FINANCIER,
    UserRole.ACCOUNTANT,
    UserRole.SALESMANAGER,
  ]);

  const canRecordPayment = hasAnyRole(roles, [
    UserRole.ADMIN,
    UserRole.FINANCIER,
    UserRole.ACCOUNTANT,
  ]);

  const canManageRelationship = hasAnyRole(roles, [
    UserRole.ADMIN,
    UserRole.ACCOUNTANT,
    UserRole.SALESMANAGER,
  ]);

  // Block LMS access — same set the backend accepts (PAYMENT_ACCESS_ROLES:
  // Admin, FinanceManager, AccountManager, SalesManager).
  const canBlockLmsAccess = hasAnyRole(roles, [
    UserRole.ADMIN,
    UserRole.FINANCIER,
    UserRole.ACCOUNTANT,
    UserRole.SALESMANAGER,
  ]);

  // Mirrors the backend's SURVEY_MANAGER_ROLES.
  const canReadStudentSurveys = hasAnyRole(roles, [
    UserRole.ADMIN,
    UserRole.ACCOUNTANT,
    UserRole.METHODIST,
  ]);

  return {
    canViewFinance,
    canExportFinance: canViewFinance,
    canRecordPayment,
    canIssueInvoice: canRecordPayment,
    canIssueFaktura: canRecordPayment,
    canAddAgreement: canManageRelationship,
    canChangeGroup: canManageRelationship,
    canContactEmail: canManageRelationship,
    canBlockLmsAccess,
    canReadStudentSurveys,
  };
};
